import { ComponentProps, forwardRef } from "react";
import { cva } from "styled-system/css";
import { styled } from "styled-system/jsx";

const input = cva({
  base: {
    appearance: "none",
    background: "white",
    borderColor: "border.light",
    borderRadius: 100,
    borderWidth: "1px",
    color: "text.dark",
    fontFamily: "heading",
    fontWeight: "semiBold",
    letterSpacing: "tightest",
    textAlign: "center",
    fontSize: { base: "2xl", md: "5xl" },
    lineHeight: { base: "xl", md: "4xl" },
    outline: 0,
    position: "relative",
    transitionDuration: "normal",
    transitionProperty: "box-shadow, border-color",
    transitionTimingFunction: "default",
    w: { base: "48px", md: "68px" },
    p: { base: "100", md: "150" },
    _disabled: {
      _placeholder: {
        color: "text.disabled",
      },
      borderColor: "border.disabled",
      color: "text.disabled",
      cursor: "not-allowed",
    },
    _focus: {
      borderColor: "border.dark",
      boxShadow: "0 0 0 1px token(colors.border.dark)",
    },
    _hover: {
      _enabled: {
        borderColor: "border.mid",
        boxShadow: "0 0 0 1px token(colors.border.mid)",
      },
      _focus: {
        borderColor: "border.dark",
        boxShadow: "0 0 0 1px token(colors.border.dark)",
      },
    },
    _invalid: {
      color: "text.error.mid",
    },
    "&[data-success='true']": {
      color: "text.success.mid",
    },
    "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
      appearance: "none", // Removes the arrows
    },
  },
});

const BaseInput = styled("input", input);

type BaseInputProps = ComponentProps<typeof BaseInput>;
interface SingleNumberInputProps extends Omit<BaseInputProps, "onChange"> {
  valid?: boolean;
  invalid?: boolean;
  onChange?: (value: string) => void;
}

export const SingleNumberInput = forwardRef<
  HTMLInputElement,
  SingleNumberInputProps
>(function SingleNumberInput(
  { valid, invalid, onChange, onFocus, onBlur, ...props },
  ref,
) {
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.placeholder = ""; // Clear placeholder on focus
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      e.target.placeholder = "-"; // Restore placeholder if input is empty
    }
    onBlur?.(e);
  };
  return (
    <BaseInput
      ref={ref}
      placeholder={"-"}
      {...props}
      type="number"
      maxLength={1}
      max={9}
      min={0}
      step={1}
      onChange={e => {
        const inputValue = e.target.value;
        if (/^\d?$/.test(inputValue)) {
          onChange?.(inputValue); // Only allow single digits
        }
      }}
      data-success={valid || undefined}
      data-invalid={invalid || undefined}
      aria-invalid={invalid || undefined}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
});
